import { useLayoutEffect, useState, type RefObject } from "react";

/** `normal` rows, tighter `compact` rows, or a `loop` when even those overflow. */
export type ListFitMode = "normal" | "compact" | "loop";

export interface ListFit {
  columns: number;
  mode: ListFitMode;
}

/** Vertical padding of a row, in rem (Tailwind `py-1.5` and `py-0.5`). */
export const ROW_PADDING_REM = { normal: 0.375, compact: 0.125 } as const;
const MIN_COLUMN_REM = 12;
const COLUMN_GAP_REM = 1.5;
const MAX_COLUMNS = 4;

/** Pure decision, given the box, one row's height without padding and the root font size. */
export function decideListFit(
  count: number,
  box: { width: number; height: number },
  rowContent: number,
  rem: number,
): ListFit {
  const byWidth = Math.floor(
    (box.width + COLUMN_GAP_REM * rem) / ((MIN_COLUMN_REM + COLUMN_GAP_REM) * rem),
  );
  const columns = Math.max(1, Math.min(MAX_COLUMNS, count, byWidth));
  const rows = Math.ceil(count / columns);
  const need = (padding: number) => rows * (rowContent + 2 * padding * rem);
  // One pixel of slack absorbs subpixel rounding.
  const room = box.height + 1;
  const mode =
    need(ROW_PADDING_REM.normal) <= room
      ? "normal"
      : need(ROW_PADDING_REM.compact) <= room
        ? "compact"
        : "loop";
  return { columns, mode };
}

/**
 * Fits `count` rows in the box `ref` points to: as many columns as its width holds
 * (filled top to bottom), tighter rows when they still overflow, then a loop. A box
 * sized by its own content always fits, so the list only adapts when its host
 * bounds its height.
 */
export function useListFit(ref: RefObject<HTMLElement | null>, count: number): ListFit {
  const [fit, setFit] = useState<ListFit>({ columns: 1, mode: "normal" });
  useLayoutEffect(() => {
    const box = ref.current;
    if (!box) return;
    const measure = () => {
      const row = box.querySelector("li");
      if (!row) return;
      const style = getComputedStyle(row);
      const rowContent =
        row.offsetHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const next = decideListFit(
        count,
        { width: box.clientWidth, height: box.clientHeight },
        rowContent,
        rem,
      );
      setFit((previous) =>
        previous.columns === next.columns && previous.mode === next.mode ? previous : next,
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [ref, count]);
  return fit;
}
